import { triggerBlobDownload } from '../../../utils/downloadFile';
import { downloadFilesAsZip, sanitizeFileName } from '../../../utils/downloadFilesAsZip';
import { buildMaTelopAssForSegment } from './buildMaTelopAss';
import { applyOptionSelectionsToSegment } from './linkMaSequence';
import { MaTelopConfig } from './maTelopConfig';
import { resolveMaTelopFont } from './maTelopFonts';
import { MaOptionSlot, MaSegment, MaSequence, stripExtension } from './maTypes';
import { RenderMaSegmentResult, renderMaSegmentToWav } from './renderMaSegment';
import { RenderVideoPhase, renderMaSegmentToVideo } from './renderMaSegmentVideo';
import { MaTimeFormat, csvTextToBlob, serializeMaTimeChartCsv } from './serializeMaCsv';

/**
 * MA 書き出しの計画生成と実行。
 *
 * 書き出しモーダル（MaExportModal）の選択
 *   対象セグメント × オプション候補の組み合わせ × 成果物
 * を 1 ファイル = 1 ジョブの一覧（planMaExport）に展開し、順番にレンダリングして
 * 単体ダウンロードまたは zip にまとめる（runMaExport）。
 *
 * - 同じセグメント × 組み合わせの WAV / 動画はミックスを 1 回だけレンダリングして共有する
 * - CSV は全セグメントが単一の組み合わせなら 1 ファイルにまとめ（従来のタイムチャート書き出し）、
 *   複数候補を選んだセグメントがあれば (セグメント × 組み合わせ) ごとに出す
 * - 失敗したジョブはスキップして続行し、最後にまとめて報告する
 */

export type MaExportDeliverable = 'telopVideo' | 'mixVideo' | 'mixWav' | 'timeChartCsv';

export const MA_EXPORT_DELIVERABLES: ReadonlyArray<{
  value: MaExportDeliverable;
  label: string;
}> = [
  { value: 'telopVideo', label: 'タイムチャート付き動画' },
  { value: 'mixVideo', label: 'ミックス動画（無劣化）' },
  { value: 'mixWav', label: 'ミックス WAV' },
  { value: 'timeChartCsv', label: 'タイムチャート CSV' },
];

export interface MaExportRequest {
  sequence: MaSequence;
  /** 対象セグメント index（昇順） */
  segmentIndexes: number[];
  deliverables: MaExportDeliverable[];
  /** スロットキー → 選択した候補キー（複数）。未指定 / 空のスロットは既定候補 */
  optionChoices: Record<string, string[]>;
  /** プレビューでの切替順（同一位置の動画候補の後勝ち解決に使う） */
  switchOrder: string[];
  telopConfig: MaTelopConfig;
  /** CSV の時刻列の形式 */
  timeFormat: MaTimeFormat;
  /** テロップ動画を 720p に縮小 */
  scaleTo720p: boolean;
  /** まとめ CSV / zip のファイル名ベース（MA ファイル名） */
  fileBaseName: string;
}

/** 1 ファイルぶんの書き出しジョブ */
export interface MaExportJob {
  kind: MaExportDeliverable;
  /** 対象セグメント index（全動画まとめ CSV は null） */
  segmentIndex: number | null;
  /** 適用するオプション選択（スロットキー → 候補キー） */
  selections: Record<string, string>;
  /** 候補ラベル（組み合わせが 1 つだけのセグメントは null） */
  label: string | null;
  /** 出力ファイル名の拡張子なし部分（動画の拡張子はレンダリング後に決まる） */
  fileStem: string;
}

export interface MaExportProgress {
  /** 処理中のジョブ番号（1 始まり） */
  current: number;
  total: number;
  message: string;
}

export interface MaExportResult {
  /** 書き出したファイル数 */
  succeeded: number;
  /** 失敗したジョブ（出力名（拡張子なし）と理由） */
  failures: Array<{ fileStem: string; reason: string }>;
  /** 音声なし / 取得不可で除外したファイル名 */
  skippedFiles: string[];
}

/** 1 セグメントのオプション候補の組み合わせ（スロットキー → 候補キー）とラベル */
export interface MaOptionCombination {
  selections: Record<string, string>;
  label: string | null;
}

/**
 * セグメントに属するスロットの選択候補から組み合わせ（直積）を作る。
 * 候補が 1 つだけのスロットはラベルに含めない（「どの候補を変えたか」だけが分かればよい）
 */
export function buildOptionCombinations(
  slots: MaOptionSlot[],
  optionChoices: Record<string, string[]>,
): MaOptionCombination[] {
  const choicesPerSlot = slots.map(slot => {
    const selected = (optionChoices[slot.key] ?? []).filter(key =>
      slot.choices.some(c => c.key === key),
    );
    const keys = selected.length > 0 ? selected : [slot.defaultChoiceKey];
    return { slot, keys };
  });

  let combos: MaOptionCombination[] = [{ selections: {}, label: null }];
  choicesPerSlot.forEach(({ slot, keys }) => {
    const next: MaOptionCombination[] = [];
    combos.forEach(combo => {
      keys.forEach(key => {
        const choice = slot.choices.find(c => c.key === key);
        const labelPart = keys.length > 1 ? (choice?.label ?? key) : null;
        next.push({
          selections: { ...combo.selections, [slot.key]: key },
          label: [combo.label, labelPart].filter((s): s is string => s != null).join('+') || null,
        });
      });
    });
    combos = next;
  });
  return combos;
}

/** 拡張子が同じになり得る成果物のグループ（このグループ内でだけ名前衝突を避ける） */
function nameGroup(kind: MaExportDeliverable): string {
  return kind === 'mixWav' ? 'wav' : kind === 'timeChartCsv' ? 'csv' : 'video';
}

/** ファイル名の衝突を連番で回避する（同一グループ内のみ） */
function uniqueName(used: Set<string>, group: string, base: string): string {
  let name = base;
  let n = 2;
  while (used.has(`${group}:${name.toLowerCase()}`)) {
    name = `${base} (${n})`;
    n += 1;
  }
  used.add(`${group}:${name.toLowerCase()}`);
  return name;
}

/** 書き出しジョブ一覧を組み立てる（純関数） */
export function planMaExport(request: MaExportRequest): MaExportJob[] {
  const { sequence, segmentIndexes, deliverables, optionChoices, fileBaseName } = request;
  const wants = (kind: MaExportDeliverable) => deliverables.includes(kind);
  const jobs: MaExportJob[] = [];
  const usedNames = new Set<string>();

  const perSegment = segmentIndexes
    .filter(i => sequence.segments[i])
    .map(segmentIndex => {
      const slots = sequence.optionSlots.filter(slot => slot.segmentIndex === segmentIndex);
      return {
        segmentIndex,
        combos: buildOptionCombinations(slots, optionChoices),
        videoName: sanitizeFileName(stripExtension(sequence.segments[segmentIndex].videoItem.file_name)),
      };
    });

  const anyMultiple = perSegment.some(s => s.combos.length > 1);

  perSegment.forEach(({ segmentIndex, combos, videoName }) => {
    combos.forEach(combo => {
      const base = combo.label ? `${videoName}_${sanitizeFileName(combo.label)}` : videoName;
      const push = (kind: MaExportDeliverable, suffix: string) =>
        jobs.push({
          kind,
          segmentIndex,
          selections: combo.selections,
          label: combo.label,
          fileStem: uniqueName(usedNames, nameGroup(kind), `${base}_${suffix}`),
        });
      if (wants('telopVideo')) push('telopVideo', 'タイムチャート');
      if (wants('mixVideo')) push('mixVideo', 'ミックス');
      if (wants('mixWav')) push('mixWav', 'ミックス');
      if (wants('timeChartCsv') && anyMultiple) push('timeChartCsv', 'TC');
    });
  });

  // 全セグメントが単一の組み合わせなら CSV は 1 ファイルにまとめる
  // （スロットキーはシーケンス内で一意なので、各セグメントの選択を 1 つの map に合成できる）
  if (wants('timeChartCsv') && !anyMultiple && perSegment.length > 0) {
    const single = perSegment.length === 1 ? perSegment[0] : null;
    jobs.push({
      kind: 'timeChartCsv',
      segmentIndex: single ? single.segmentIndex : null,
      selections: Object.assign({}, ...perSegment.map(s => s.combos[0].selections)),
      label: null,
      fileStem: uniqueName(
        usedNames,
        'csv',
        `${single ? single.videoName : sanitizeFileName(fileBaseName)}_TC`,
      ),
    });
  }

  return jobs;
}

// ===== 実行 =====

const PHASE_MESSAGES: Record<RenderVideoPhase, string> = {
  load: '書き出しエンジンを準備中…（初回はダウンロードに時間がかかります）',
  mix: 'オーディオをレンダリング中…',
  fetch: '動画ファイルを取得中…',
  mux: '動画を書き出し中…',
  encode: 'テロップを焼き込み中…',
};

let running = false;

/** 書き出しが進行中か（二重実行ガード。UI 側のメニュー無効化にも使える） */
export function isMaExportRunning(): boolean {
  return running;
}

/** オプション選択を適用したセグメントを解決する */
function resolveSegment(
  sequence: MaSequence,
  segmentIndex: number,
  selections: Record<string, string>,
  switchOrder: string[],
): MaSegment {
  const segment = sequence.segments[segmentIndex];
  const slots = sequence.optionSlots.filter(slot => slot.segmentIndex === segmentIndex);
  if (slots.length === 0) return segment;
  return applyOptionSelectionsToSegment(segment, slots, selections, switchOrder);
}

/** 指定セグメントにオプション選択を適用したシーケンス（他のセグメントはそのまま） */
function withResolvedSegments(
  sequence: MaSequence,
  segmentIndexes: number[],
  selections: Record<string, string>,
  switchOrder: string[],
): MaSequence {
  const segments = sequence.segments.map((segment, idx) =>
    segmentIndexes.includes(idx) ? resolveSegment(sequence, idx, selections, switchOrder) : segment,
  );
  return { ...sequence, segments };
}

/** タイムチャート CSV ジョブの本文を生成する */
function buildCsvForJob(request: MaExportRequest, job: MaExportJob): string {
  const { sequence, switchOrder, timeFormat } = request;
  // segmentIndex が null = 全動画まとめ（対象セグメントそれぞれに単一の組み合わせを適用）
  const targets = job.segmentIndex == null ? request.segmentIndexes : [job.segmentIndex];
  const resolved = withResolvedSegments(sequence, targets, job.selections, switchOrder);
  return serializeMaTimeChartCsv(resolved, { segmentIndex: job.segmentIndex, timeFormat });
}

/**
 * セグメント × 組み合わせ単位のレンダリングキャッシュ。
 * 同じ組み合わせの WAV / 動画ジョブが連続するため、ミックスと動画バイト列を使い回す
 */
interface SegmentRenderCache {
  key: string;
  segment: MaSegment;
  mix: RenderMaSegmentResult | null;
  videoBytes: Map<string, Uint8Array>;
}

function cacheKeyFor(job: MaExportJob): string {
  return `${job.segmentIndex}:${JSON.stringify(job.selections)}`;
}

/** 動画ジョブ（テロップ付き / ミックス）をレンダリングする */
async function renderVideoForJob(
  request: MaExportRequest,
  job: MaExportJob,
  cache: SegmentRenderCache & { mix: RenderMaSegmentResult },
  report: (_message: string) => void,
): Promise<{ blob: Blob; extension: string }> {
  const { sequence, telopConfig, scaleTo720p } = request;
  const isTelop = job.kind === 'telopVideo';
  const font = resolveMaTelopFont(telopConfig.fontKey);
  const { segment } = cache;
  const progressLabel = isTelop ? PHASE_MESSAGES.encode : PHASE_MESSAGES.mux;

  return renderMaSegmentToVideo(segment, {
    mode: isTelop ? 'telop' : 'remux',
    telop: isTelop
      ? {
          font,
          buildAss: frame =>
            buildMaTelopAssForSegment(segment, {
              fps: sequence.fps,
              config: telopConfig,
              displayNames: sequence.displayNames,
              playResX: frame.width,
              playResY: frame.height,
              fontName: font.assFontName,
            }),
        }
      : undefined,
    scaleTo720p,
    fps: sequence.fps,
    mix: cache.mix,
    videoBytesCache: cache.videoBytes,
    onPhase: phase => report(PHASE_MESSAGES[phase]),
    onRenderProgress: ratio => report(`${progressLabel} ${Math.round(ratio * 100)}%`),
  });
}

export async function runMaExport(
  request: MaExportRequest,
  onProgress?: (_progress: MaExportProgress) => void,
): Promise<MaExportResult> {
  if (running) throw new Error('別の書き出しが進行中です');
  running = true;
  try {
    const jobs = planMaExport(request);
    const { sequence, switchOrder, fileBaseName } = request;
    const outputs: Array<{ path: string; blob: Blob }> = [];
    const failures: MaExportResult['failures'] = [];
    const skipped = new Set<string>();
    let cache: SegmentRenderCache | null = null;

    for (let i = 0; i < jobs.length; i += 1) {
      const job = jobs[i];
      const report = (message: string) =>
        onProgress?.({ current: i + 1, total: jobs.length, message });

      try {
        if (job.kind === 'timeChartCsv') {
          report('タイムチャート CSV を生成中…');
          outputs.push({
            path: `${job.fileStem}.csv`,
            blob: csvTextToBlob(buildCsvForJob(request, job)),
          });
          continue;
        }

        // セグメント × 組み合わせが変わったらキャッシュを作り直す
        const segmentIndex = job.segmentIndex as number;
        if (!cache || cache.key !== cacheKeyFor(job)) {
          cache = {
            key: cacheKeyFor(job),
            segment: resolveSegment(sequence, segmentIndex, job.selections, switchOrder),
            mix: null,
            videoBytes: new Map(),
          };
        }
        if (!cache.mix) {
          report(PHASE_MESSAGES.mix);
          cache.mix = await renderMaSegmentToWav(cache.segment);
        }
        const ready = cache as SegmentRenderCache & { mix: RenderMaSegmentResult };
        ready.mix.skippedFiles.forEach(name => skipped.add(name));

        if (job.kind === 'mixWav') {
          outputs.push({ path: `${job.fileStem}.wav`, blob: ready.mix.blob });
          continue;
        }

        const { blob, extension } = await renderVideoForJob(request, job, ready, report);
        outputs.push({ path: `${job.fileStem}.${extension}`, blob });
      } catch (e) {
        console.warn(`[MA] 書き出しに失敗: ${job.fileStem}`, e);
        failures.push({
          fileStem: job.fileStem,
          reason: e instanceof Error ? e.message : '不明なエラー',
        });
      }
    }

    // 出力: 1 件なら単体ダウンロード、複数なら zip
    if (outputs.length === 1) {
      triggerBlobDownload(outputs[0].blob, outputs[0].path);
    } else if (outputs.length > 1) {
      onProgress?.({ current: jobs.length, total: jobs.length, message: 'zip にまとめています…' });
      const single = request.segmentIndexes.length === 1 ? request.segmentIndexes[0] : null;
      const zipBase =
        single != null
          ? stripExtension(sequence.segments[single].videoItem.file_name)
          : fileBaseName;
      await downloadFilesAsZip(outputs, `${sanitizeFileName(zipBase)}_書き出し.zip`);
    }

    return { succeeded: outputs.length, failures, skippedFiles: Array.from(skipped) };
  } finally {
    running = false;
  }
}
